import { WeightableEntityWithResult } from '@upright/common/model/output/scores/model-entity-data/types/types-weightable';
import { compact, isNil, range, sumBy } from 'lodash';
import { pickDefaultModelResult } from '../../../../../../../common/model/data-point-pickers';
import { computeNetImpactRatio } from '../../../../../../../common/model/output/scores/compute-net-score';
import { ModelEntityWithScores } from '../../../../../../../common/model/output/scores/model-entity-data/types';
import { FinalImpactScoreType } from '../../../../../../../common/model/output/scores/types';
import { NumberHash } from '../../../../../../../common/utils/number-hash/types';
import { Histogram, RatingsHistogramDatum } from './histogram';
import { simpleRatings, toSimpleCompanyNetImpactRating } from './rating-scale';

interface Props {
  scoreData: ModelEntityWithScores | undefined;
  itemData: WeightableEntityWithResult[] | null | undefined;
  loadingFinished: boolean;
}

export function HoldingsHistogram(props: Props) {
  const itemData = getItemDataToDisplay(props.scoreData, props.itemData, props.loadingFinished);
  const ratingsData = toRatingsData(compact(itemData));

  return (
    <div>
      <Histogram
        data={ratingsData}
        marginLeft={30}
        marginRight={0}
        marginBottom={45}
        marginTop={0}
        labelFormatter={() => ''}
      />
    </div>
  );
}

function pickRatio(item: WeightableEntityWithResult) {
  const result = pickDefaultModelResult(item.modelResults);
  if (!result.impactScoresByTIVV || isNil(result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE])) {
    return;
  }
  const scores = result.impactScoresByTIVV[FinalImpactScoreType.RELATIVE];
  const ratio = computeNetImpactRatio(scores);
  return ratio;
}

function pickRating(item: WeightableEntityWithResult) {
  const ratio = pickRatio(item);
  if (!ratio) {
    return undefined;
  }
  return toSimpleCompanyNetImpactRating(ratio);
}

function toWeightByRating(data: WeightableEntityWithResult[]): NumberHash {
  const weightByRating: NumberHash = {};
  const totalWeight = sumBy(data, d => d.weight || 0);
  data.forEach(item => {
    const rating = pickRating(item);
    if (rating) {
      if (!weightByRating[rating]) {
        weightByRating[rating] = 0;
      }
      weightByRating[rating] += (item.weight ?? 0) / totalWeight;
    }
  });
  return weightByRating;
}

function toRatingsData(data: WeightableEntityWithResult[]): RatingsHistogramDatum[] {
  const weightByRating = toWeightByRating(data);
  return simpleRatings.map(rating => {
    return {
      rating,
      weight: weightByRating[rating] || 0,
    };
  });
}

function getItemDataToDisplay(
  scoreData: ModelEntityWithScores | undefined,
  itemData: WeightableEntityWithResult[] | null | undefined,
  loadingFinished: boolean,
): WeightableEntityWithResult[] | undefined[] {
  const limit = Number.MAX_SAFE_INTEGER;

  const itemDataToDisplay = (() => {
    if (!loadingFinished) {
      return range((scoreData && Math.min(limit, scoreData.modelResults[0]?.itemCount || 0)) || 5).map(() => undefined);
    }
    return itemData ?? [];
  })();
  return itemDataToDisplay;
}
